/**
 * K2 — Detail header workbench actions via resolveWorkbenchActions + CR4 batch preview
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { UniversalBaseEntity } from '@shared/types/entity';
import type { CapabilityDecision } from '@shared/capability';
import {
  buildCapabilityPreviewContexts,
  buildStartFlowActions,
  getStartFlowBindings,
  previewInputsToBatchContexts,
  registerWorkbenchActions,
  resolveActions,
  resolveWorkbenchActions,
  type WorkbenchActionSurface,
  type WorkbenchResolvedAction,
} from '@shared/business-workbench';
import type { PathPlane } from '@shared/tenant-host/pathPlanes';
import type { UserRole } from '@shared/types/roles';
import { collectTablePermissionKeys } from '../../components/common/DynamicTable/core/context/helpers/capability-preview-helpers';
import { fetchCapabilityBatchResolve } from '../../services/capabilityApi';
import { useTablePermissionContext } from '../ui/useTablePermissionContext';
import { STEP_UP_MFA_COMPLETED_EVENT } from '../../workbench/step-up-events';

export interface UseDetailWorkbenchActionsOptions {
  tenantId: string;
  entity: UniversalBaseEntity | null;
  businessDomain: string;
  role: UserRole;
  plane?: PathPlane;
  permissionKeys?: readonly string[];
  surface?: WorkbenchActionSurface;
  enabled?: boolean;
}

export interface UseDetailWorkbenchActionsResult {
  actions: WorkbenchResolvedAction[];
  readDeniedAction: WorkbenchResolvedAction | undefined;
  loading: boolean;
}

export function useDetailWorkbenchActions({
  tenantId,
  entity,
  businessDomain,
  role,
  plane = 'business',
  permissionKeys: permissionKeysProp,
  surface = 'detail-header',
  enabled = true,
}: UseDetailWorkbenchActionsOptions): UseDetailWorkbenchActionsResult {
  const [actions, setActions] = useState<WorkbenchResolvedAction[]>([]);
  const [loading, setLoading] = useState(false);
  const [stepUpTick, setStepUpTick] = useState(0);
  const requestSeq = useRef(0);

  const userPermissionContext = useTablePermissionContext(businessDomain);

  const derivedPermissionKeys = useMemo(
    () => collectTablePermissionKeys(userPermissionContext, businessDomain),
    [userPermissionContext, businessDomain]
  );

  const permissionKeysSignature =
    permissionKeysProp !== undefined
      ? permissionKeysProp.join('\0')
      : derivedPermissionKeys.join('\0');

  const permissionKeys = useMemo(() => {
    if (permissionKeysProp !== undefined) {
      return permissionKeysSignature.length > 0 ? permissionKeysSignature.split('\0') : [];
    }
    return derivedPermissionKeys;
  }, [permissionKeysSignature, derivedPermissionKeys, permissionKeysProp]);

  const resolveInput = useMemo(
    () => ({
      role,
      status: entity ? String(entity.status) : 'active',
      plane,
      businessDomain,
      surface,
      ...(permissionKeys.length > 0 ? { permissionKeys } : {}),
    }),
    [role, entity?.status, plane, businessDomain, permissionKeysSignature, surface]
  );

  useEffect(() => {
    const onStepUp = () => setStepUpTick((prev) => prev + 1);
    window.addEventListener(STEP_UP_MFA_COMPLETED_EVENT, onStepUp);
    return () => window.removeEventListener(STEP_UP_MFA_COMPLETED_EVENT, onStepUp);
  }, []);

  useEffect(() => {
    if (!enabled || !entity) {
      setActions([]);
      setLoading(false);
      return;
    }

    const startFlowBindings = getStartFlowBindings(entity);
    registerWorkbenchActions(buildStartFlowActions(startFlowBindings));

    const baseActions = resolveActions(entity, resolveInput);
    const scope = { domain: businessDomain, objectId: entity.id };
    const previewInputs = buildCapabilityPreviewContexts(
      baseActions.map((workbenchAction) => ({ workbenchAction, scope }))
    );

    const seq = ++requestSeq.current;
    let cancelled = false;

    const applyResolved = (decisions?: readonly CapabilityDecision[]): void => {
      const resolved = resolveWorkbenchActions(entity, resolveInput, {
        ...(permissionKeys.length > 0 ? { permissionKeys } : {}),
        ...(previewInputs.length > 0 ? { previewInputs } : {}),
        ...(decisions !== undefined && decisions.length > 0 ? { decisions } : {}),
      });
      if (!cancelled && seq === requestSeq.current) {
        setActions(resolved);
      }
    };

    if (previewInputs.length === 0) {
      applyResolved();
      setLoading(false);
      return;
    }

    const load = async (): Promise<void> => {
      setLoading(true);
      try {
        const decisions = await fetchCapabilityBatchResolve(
          tenantId,
          previewInputsToBatchContexts(previewInputs, plane)
        );
        if (cancelled || seq !== requestSeq.current) {
          return;
        }
        applyResolved(decisions);
      } catch {
        if (cancelled || seq !== requestSeq.current) {
          return;
        }
        applyResolved();
      } finally {
        if (!cancelled && seq === requestSeq.current) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    entity?.id,
    entity?.status,
    tenantId,
    businessDomain,
    plane,
    permissionKeysSignature,
    resolveInput,
    stepUpTick,
  ]);

  const readDeniedAction = useMemo(() => {
    const view = actions.find((action) => action.id === 'view');
    return view?.forbidden ? view : undefined;
  }, [actions]);

  return { actions, readDeniedAction, loading };
}
